import { AvatarSizeProps } from 'components/avatar'
import { defaultBgImageBase64, placeholderImage } from 'config/constants'
import { forwardRef, RefObject, useEffect, useState } from 'react'
import { styled, CSS } from 'theme'

const ImageCss = styled('img', {
  // Reset
  borderRadius: '$radii$rounded',
  objectFit: 'contain',

  variants: {
    border: {
      true: { border: '1px solid $gray300', background: '$gray100' },
      false: { border: 'none' },
    },
    size: {
      xxs: {
        width: '$size$16',
        height: '$size$16',
      },
      xs: {
        width: '$size$20',
        height: '$size$20',
      },
      sm: {
        width: '$size$24',
        height: '$size$24',
      },
      md: {
        width: '$size$32',
        height: '$size$32',
      },
      lg: {
        width: '$size$40',
        height: '$size$40',
      },
      xl: {
        width: '$size$64',
        height: '$size$64',
      },
      xxl: {},
      login: {
        width: '$size$48',
        height: '$size$48',
      },
      level1: {
        width: '$size$48',
        height: '$size$48',
      },
      level2: {
        width: '$size$48',
        height: '$size$48',
      },
      level3: {
        width: '$size$48',
        height: '$size$48',
      },
      full: {
        width: '100%',
        height: '100%',
        borderRadius: 0,
        boxShadow: 'none',
      },
      auto: {
        width: 'auto',
        height: 'auto',
        borderRadius: 0,
        boxShadow: 'none',
      },
      100: {
        width: 100,
        height: 100,
        objectFit: 'cover',
        borderRadius: '$radii$xs',
        boxShadow: 'none',
      },
      120: {
        width: 120,
        height: 120,
      },
      160: {
        width: 160,
        height: 160,
      },
      rectangle: {
        width: 180,
        height: 'auto',
        objectFit: 'contain',
        boxShadow: 'none',
      },
      logo: {
        width: 280,
        height: 'auto',
        objectFit: 'contain',
        boxShadow: 'none',
      },
      container: {},
    },
  },
  defaultVariants: {
    size: 'full',
    border: false,
  },
})

export interface ImageProps {
  className?: string
  size?:
    | AvatarSizeProps
    | 'xxl'
    | 'full'
    | 'auto'
    | '100'
    | '120'
    | 'rectangle'
    | 'logo'
  url: string
  css?: CSS
  isIcon?: boolean
  border?: boolean
  ref?: RefObject<HTMLImageElement>
  onLoad?: (e: React.SyntheticEvent<HTMLImageElement>) => void
}

export const Image = forwardRef<HTMLImageElement, ImageProps>((props, ref) => {
  const { className, size, css, isIcon, border, url, onLoad } = props

  const [imageSrc, setImageSrc] = useState(url)
  const [iconCss, setIconCss] = useState({})
  const [error, setError] = useState(false)

  useEffect(() => {
    setImageSrc(url)
  }, [url])

  const handleImageError = () => {
    isIcon ? setImageSrc(placeholderImage) : setImageSrc(defaultBgImageBase64)
    setIconCss(isIcon ? {} : {})
    setError?.(true)
  }

  const splitUrl = typeof url === 'string' ? url.split('/') : []
  const alternative = splitUrl.length > 0 ? splitUrl[splitUrl.length - 1] : ''
  const alternativeText = alternative.includes('.')
    ? alternative.split('.')[0]
    : 'default-alt'

  return (
    <ImageCss
      onError={handleImageError}
      className={error ? `hasImageError ${className}` : className}
      size={size}
      border={border}
      src={imageSrc}
      alt={alternativeText}
      css={{ ...css, ...iconCss }}
      ref={ref}
      onLoad={onLoad}
    />
  )
})
